
/**
 * Safe Navigation Utilities
 * Prevents open redirect vulnerabilities and provides consistent navigation patterns
 */

import { ROUTES } from '../domain/routes';

// Allowed external domains for redirects
const ALLOWED_EXTERNAL_DOMAINS = [
  'mercadopago.com',
  'mercadopago.com.ar',
  'www.mercadopago.com',
  'www.mercadopago.com.ar',
  'mpago.la',
  'checkout.mercadopago.com',
];

// Allowed internal paths
const ALLOWED_INTERNAL_PATHS = Object.values(ROUTES).flatMap(route => {
  if (typeof route === 'string') return [route];
  if (typeof route === 'object') return Object.values(route);
  return [];
});

/**
 * Validates if a URL is safe for navigation
 * @param url - URL to validate
 * @returns boolean indicating if URL is safe
 */
export function isSafeUrl(url: string): boolean {
  try {
    const urlObj = new URL(url, window.location.origin);
    
    // Check if it's an internal URL
    if (urlObj.origin === window.location.origin) {
      // Check if path is in allowed routes
      const path = urlObj.pathname;
      return ALLOWED_INTERNAL_PATHS.some(allowedPath => 
        path === allowedPath || path.startsWith(allowedPath.replace(/:\w+/g, ''))
      );
    }
    
    // Check if it's an allowed external domain
    return ALLOWED_EXTERNAL_DOMAINS.some(domain => 
      urlObj.hostname === domain || urlObj.hostname.endsWith(`.${domain}`)
    );
  } catch {
    return false;
  }
}

/**
 * Safely navigates to a URL
 * @param url - URL to navigate to
 * @param options - Navigation options
 * @throws Error if URL is not safe
 */
export function safeNavigate(url: string, options: { 
  replace?: boolean;
  external?: boolean;
} = {}): void {
  if (!isSafeUrl(url)) {
    console.error(`[SafeNavigate] Blocked unsafe navigation to: ${url}`);
    throw new Error(`Navigation to ${url} is not allowed`);
  }
  
  if (options.external) {
    // For external URLs, open in same tab (for payment flows)
    window.location.href = url;
  } else if (options.replace) {
    window.location.replace(url);
  } else {
    window.location.href = url;
  }
}

/**
 * Safely reloads the current page
 * Use this instead of window.location.reload()
 */
export function safeReload(): void {
  window.location.reload();
}

/**
 * Gets the current URL safely
 * Returns empty string if window is not available (SSR)
 */
export function getCurrentUrl(): string {
  if (typeof window === 'undefined') return '';
  return window.location.href;
}

/**
 * Gets the current origin safely
 * Returns empty string if window is not available (SSR)
 */
export function getCurrentOrigin(): string {
  if (typeof window === 'undefined') return '';
  return window.location.origin;
}

/**
 * Gets URL search params safely
 */
export function getSearchParams(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

/**
 * Navigation result type for payment flows
 */
export interface PaymentRedirectUrls {
  success: string;
  failure: string;
  pending: string;
}

/**
 * Generates safe redirect URLs for payment flows
 * @param paymentId - Payment ID to include in URLs
 * @returns Safe redirect URLs
 */
export function generatePaymentRedirectUrls(paymentId?: string): PaymentRedirectUrls {
  const origin = getCurrentOrigin();
  const baseSuccess = `${origin}${ROUTES.SUCCESS}`;
  const basePayment = `${origin}${ROUTES.PAYMENT}`;
  
  return {
    success: paymentId ? `${baseSuccess}?payment_id=${encodeURIComponent(paymentId)}` : baseSuccess,
    failure: `${basePayment}?status=failed`,
    pending: `${basePayment}?status=pending`,
  };
}

/**
 * Hook for safe navigation (for use in React components)
 * Combines with react-router's navigate for internal navigation
 */
export function useSafeNavigation() {
  return {
    isSafeUrl,
    safeNavigate,
    safeReload,
    getCurrentUrl,
    getCurrentOrigin,
    getSearchParams,
    generatePaymentRedirectUrls,
  };
}
